import { useTranslation } from "react-i18next";
// src/pages/ReceivablesList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { ArrowsUpDownIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import { apiGet } from "../api/client";
import EmptyState from "../components/EmptyState";
import { companyRoute } from "../utils/routeHelpers";
import {
  ListToolbar,
  type MenuItem,
  type SortOption,
} from "../components/ListToolbar";
import { downloadCsv } from "../utils/downloadCsv";
import { formatAnyDateToLocale } from "../utils/dateUtils";
import { A, pillClass } from "../styles/appleClasses";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { Button } from "../components/Button";
import ListTableShell from "../components/lists/ListTableShell";

interface InvoiceDetail {
  invoice_id: number;
  invoice_number: string;
  sale_id: number;
  due_date: string | null;
  amount_due: number;
  status: string;
}

interface ReceivableSummary {
  customer_id: number;
  name: string;
  total_due: number; // may be inflated by API; we recompute from invoices
  aging_overdue: number;
  aging_10: number;
  aging_20: number;
  aging_30: number;
  invoices: InvoiceDetail[];
}

const ROWS_PER_PAGE = 10;
const PREVIEW_MOBILE = 6;
const PREVIEW_DESKTOP = 8;

type SortKey = "total" | "customer" | "overdue";
type SortDir = "asc" | "desc";

type InvoiceSortKey = "due" | "amount";
type InvoiceSortDir = "asc" | "desc";

/** --- Helpers to avoid double counting --- */
function uniqueInvoices(invoices: InvoiceDetail[]) {
  const seen = new Set<string>();
  const out: InvoiceDetail[] = [];

  for (const inv of invoices || []) {
    const key =
      inv.invoice_id != null ? `inv-${inv.invoice_id}` : `sale-${inv.sale_id}`;

    if (!seen.has(key)) {
      seen.add(key);
      out.push(inv);
    }
  }

  return out;
}

function sumCustomerDue(rec: ReceivableSummary) {
  return uniqueInvoices(rec.invoices).reduce(
    (s, inv) => s + (inv?.amount_due || 0),
    0,
  );
}

function sumAllDue(recs: ReceivableSummary[]) {
  const seen = new Set<string>();
  let sum = 0;

  for (const rec of recs || []) {
    for (const inv of rec.invoices || []) {
      const key =
        inv.invoice_id != null
          ? `inv-${inv.invoice_id}`
          : `sale-${inv.sale_id}`;

      if (!seen.has(key)) {
        seen.add(key);
        sum += inv?.amount_due || 0;
      }
    }
  }

  return sum;
}

/** --- Date formatting --- */
function formatDate(d: string | null) {
  return formatAnyDateToLocale(d);
}

function sortInvoices(
  invoices: InvoiceDetail[],
  sort: { key: InvoiceSortKey; dir: InvoiceSortDir },
) {
  const sign = sort.dir === "asc" ? 1 : -1;

  const toTime = (d: string | null) => {
    if (!d) return Number.POSITIVE_INFINITY;
    const t = new Date(d).getTime();
    return isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };

  return invoices.slice().sort((a, b) => {
    if (sort.key === "amount") {
      const av = Number(a.amount_due ?? 0);
      const bv = Number(b.amount_due ?? 0);
      if (av !== bv) return (av - bv) * sign;
    } else {
      const ta = toTime(a.due_date);
      const tb = toTime(b.due_date);
      if (ta !== tb) return (ta - tb) * sign;
    }

    // tie-breakers: invoice number, then id
    const cmpInv = (a.invoice_number || "").localeCompare(
      b.invoice_number || "",
      undefined,
      {
        sensitivity: "base",
      },
    );
    if (cmpInv !== 0) return cmpInv;
    return (a.invoice_id || 0) - (b.invoice_id || 0);
  });
}

function csvEscape(v: unknown) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function ReceivablesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [receivables, setReceivables] = useState<ReceivableSummary[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [showAllInvoices, setShowAllInvoices] = useState<
    Record<number, boolean>
  >({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"directory.customers.noCompany" | "receivables.loadError" | null>(null);

  // Toolbar state
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("total");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Invoice sort (applies to ALL expanded invoice lists)
  const [invoiceSort, setInvoiceSort] = useState<{
    key: InvoiceSortKey;
    dir: InvoiceSortDir;
  }>({
    key: "due",
    dir: "asc",
  });

  // Paging (1-based)
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!company_id) {
      setError("directory.customers.noCompany");
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet<ReceivableSummary[]>(`/ar-ap/receivables?company_id=${company_id}`)
      .then((data) => {
        setReceivables(data || []);
        setError(null);
      })
      .catch(() => setError("receivables.loadError"))
      .finally(() => setLoading(false));
  }, [company_id]);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, sortDir, receivables.length]);

  const filteredReceivables = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return receivables;

    return receivables.filter((rec) => {
      const matchCustomer = (rec.name || "").toLowerCase().includes(q);
      const matchInvoice = (rec.invoices || []).some((inv) =>
        (inv.invoice_number || "").toLowerCase().includes(q),
      );
      return matchCustomer || matchInvoice;
    });
  }, [receivables, search]);

  const sortedReceivables = useMemo(() => {
    const list = filteredReceivables.slice();

    list.sort((a, b) => {
      if (sortKey === "customer") {
        const cmp = (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });
        return sortDir === "asc" ? cmp : -cmp;
      }

      if (sortKey === "overdue") {
        const diff = (a.aging_overdue || 0) - (b.aging_overdue || 0);
        if (diff !== 0) return sortDir === "asc" ? diff : -diff;

        const t = sumCustomerDue(a) - sumCustomerDue(b);
        if (t !== 0) return sortDir === "asc" ? t : -t;

        return (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });
      }

      // sortKey === "total"
      const diff = sumCustomerDue(a) - sumCustomerDue(b);
      if (diff !== 0) return sortDir === "asc" ? diff : -diff;

      const od = (a.aging_overdue || 0) - (b.aging_overdue || 0);
      if (od !== 0) return sortDir === "asc" ? od : -od;

      return (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    });

    return list;
  }, [filteredReceivables, sortKey, sortDir]);

  const totalFiltered = sortedReceivables.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedReceivables = sortedReceivables.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );

  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  const totalReceivables = useMemo(
    () => sumAllDue(filteredReceivables),
    [filteredReceivables],
  );

  // ✅ ListToolbar wiring (no date range)
  const sortValue = `${sortKey}_${sortDir}`;
  const sortOptions: SortOption[] = [
    { label: t("receivables.sort.totalHigh"), value: "total_desc" },
    { label: t("receivables.sort.totalLow"), value: "total_asc" },
    { label: t("receivables.sort.customerAsc"), value: "customer_asc" },
    { label: t("receivables.sort.customerDesc"), value: "customer_desc" },
    { label: t("receivables.sort.overdueHigh"), value: "overdue_desc" },
    { label: t("receivables.sort.overdueLow"), value: "overdue_asc" },
  ];

  const onToolbarSortChange = (v: string) => {
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  const onHeaderSort = (k: SortKey) => {
    if (sortKey !== k) {
      setSortKey(k);
      setSortDir(k === "customer" ? "asc" : "desc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const sortArrow = (k: SortKey) =>
    sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : null;

  const clearFilters = () => {
    setSearch("");
    setSortKey("total");
    setSortDir("desc");
    setExpanded({});
    setShowAllInvoices({});
  };

  const toggleExpanded = (customerId: number) => {
    setExpanded((exp) => {
      const nextOpen = !exp[customerId];

      if (!nextOpen) {
        setShowAllInvoices((m) => {
          const next = { ...m };
          delete next[customerId];
          return next;
        });
      }

      return { ...exp, [customerId]: nextOpen };
    });
  };

  const toggleInvoiceSortDue = () => {
    setInvoiceSort((s) =>
      s.key === "due"
        ? { key: "due", dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: "due", dir: "asc" },
    );
  };

  const toggleInvoiceSortAmount = () => {
    setInvoiceSort((s) =>
      s.key === "amount"
        ? { key: "amount", dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: "amount", dir: "desc" },
    );
  };

  const exportCsv = () => {
    const rows = sortedReceivables.map((rec) => {
      const invoices = uniqueInvoices(rec.invoices);
      const totalDue = sumCustomerDue(rec);

      return {
        customer: rec.name || "",
        overdue: rec.aging_overdue || 0,
        next10d: rec.aging_10 || 0,
        d11_20: rec.aging_20 || 0,
        d21_30: rec.aging_30 || 0,
        total_due: totalDue,
        invoice_count: invoices.length,
      };
    });

    const header = [
      "Customer",
      "Overdue",
      "10D",
      "20D",
      "30D",
      "Total",
      "Invoice Count",
    ];

    const lines = [
      header.join(","),
      ...rows.map((r) =>
        [
          csvEscape(r.customer),
          r.overdue.toFixed(2),
          r.next10d.toFixed(2),
          r.d11_20.toFixed(2),
          r.d21_30.toFixed(2),
          r.total_due.toFixed(2),
          String(r.invoice_count),
        ].join(","),
      ),
    ];

    downloadCsv("receivables", lines.join("\n"));
  };

  const newItems: MenuItem[] = company_id
    ? [
        {
          label: t("sales.common.newSale"),
          onClick: () => navigate(companyRoute(company_id, "/sales/new")),
        },
      ]
    : [];

  const moreItems: MenuItem[] = [{ label: t("directory.common.exportCsv"), onClick: exportCsv }];

  const PAGE_W = "max-w-5xl";
  const money = (n: number | null | undefined) =>
    `${formatMoney(Number(n ?? 0))}`;

  // derived (no cx)
  const iconBtnCls =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const iconBtnSmCls =
    "h-9 w-9 rounded-xl inline-flex items-center justify-center " +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const linkCls = "text-[13px] font-normal text-appleblue hover:text-appleblueD";

  const thCls = "py-2 text-xs text-gray-500";
  const thBtnCls = "inline-flex items-center gap-1 hover:text-gray-700";

  const tdNumCls = `py-2 text-right font-medium ${A.num}`;
  const tdTotalCls = `py-2 pl-4 pr-6 text-right font-semibold text-gray-900 ${A.num}`;

  const sortPillBtnCls = (active: boolean) =>
    `${(A as any).pillBase || "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-none ring-1"} transition hover:bg-gray-50 ` +
    (active
      ? "bg-white text-gray-700 ring-black/10"
      : "bg-transparent text-gray-500 ring-black/5");

  const statusPill = (statusRaw: string | null | undefined) => {
    const s = (statusRaw || "").toLowerCase();
    if (s === "paid") return { label: t("sales.list.paid"), cls: pillClass("success") };
    if (s === "open") return { label: t("receivables.status.open"), cls: pillClass("warning") };
    if (s === "due soon") return { label: t("receivables.status.dueSoon"), cls: pillClass("neutral") };
    if (s === "closed") return { label: t("receivables.status.closed"), cls: pillClass("neutral") };
    if (s === "overdue")
      return {
        label: t("purchases.status.overdue"),
        cls: `${(A as any).pillBase || "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-none ring-1"} bg-red-50 text-red-700 ring-red-100`,
      };
    return { label: statusRaw || "—", cls: pillClass("neutral") };
  };

  const pagerText = (
    <span className={`${A.label} text-gray-500 select-none`}>
      {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
    </span>
  );

  const pagerBtnCls = "px-2 active:scale-100";

  if (loading) {
    return (
      <div className={`text-center py-6 text-gray-600 ${A.body}`}>
        {t("receivables.loading")}
      </div>
    );
  }
  if (error) return <EmptyState message={t(error)} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;
  if (!company_id) return <EmptyState message={t("directory.customers.noCompany")} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;

  if (!receivables.length) {
    return (
      <EmptyState
        message={t("receivables.empty")}
        heading={t("directory.common.nothingHere")}
        backLabel={t("directory.common.goBack")}
        showNewButton
        newButtonText={t("sales.list.newSaleCta")}
        newButtonPath={companyRoute(company_id, "/sales/new")}
      />
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${A.body}`}
    >
      {/* Sticky header (locked spacing: px-2 + mb-2) */}
      <div
        className={`sticky top-0 z-30 bg-gray-50 w-full ${PAGE_W} flex items-center border-b border-gray-100 px-2 pb-2 mb-2`}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`flex-1 text-center tracking-tight ${A.title}`}>
          {t("navigation.receivables")}
        </h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* ListToolbar */}
      <ListToolbar
        maxWidthClass={PAGE_W}
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("receivables.search")}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={onToolbarSortChange}
        showDateRange={false}
        newItems={newItems}
        moreItems={moreItems}
        rightSummary={
          <div className="text-right leading-tight">
            <div className={A.label}>{t("receivables.totalReceivables")}</div>
            <div className={`text-[13px] font-semibold text-gray-900 ${A.num}`}>
              {money(totalReceivables)}
            </div>
          </div>
        }
      />

      {/* Filter produced no results */}
      {receivables.length > 0 && totalFiltered === 0 ? (
        <div className={`w-full ${PAGE_W} px-2`}>
          <div className={`${A.card} p-6 text-center`}>
            <div className="text-[13px] text-gray-600">{t("directory.common.noResults")}</div>
            <button
              type="button"
              onClick={clearFilters}
              className={`${linkCls} ml-2`}
            >
              {t("directory.common.clearFilters")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className={`w-full ${PAGE_W} px-2 grid gap-3 md:hidden`}>
            {pagedReceivables.map((rec) => {
              const customerTotal = sumCustomerDue(rec);

              const invoices = sortInvoices(
                uniqueInvoices(rec.invoices),
                invoiceSort,
              );
              const showAll = !!showAllInvoices[rec.customer_id];
              const displayInvoices = showAll
                ? invoices
                : invoices.slice(0, PREVIEW_MOBILE);

              return (
                <div
                  key={rec.customer_id}
                  className={`${A.card} min-w-0 p-4 flex flex-col`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-900 truncate">
                      {rec.name}
                    </div>

                    <button
                      className={iconBtnSmCls}
                      onClick={() => toggleExpanded(rec.customer_id)}
                      aria-label={t("receivables.toggleInvoices")}
                      type="button"
                    >
                      {expanded[rec.customer_id] ? (
                        <ChevronDownIcon className="w-5 h-5 text-appleblue" />
                      ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                    <div>
                      {t("purchases.status.overdue")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {rec.aging_overdue > 0 ? money(rec.aging_overdue) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("receivables.aging10")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {rec.aging_10 > 0 ? money(rec.aging_10) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("receivables.aging20")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {rec.aging_20 > 0 ? money(rec.aging_20) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("receivables.aging30")}:{" "}
                      <span className={`font-medium text-gray-800 ${A.num}`}>
                        {rec.aging_30 > 0 ? money(rec.aging_30) : "—"}
                      </span>
                    </div>

                    <div
                      className={`ml-auto font-semibold text-gray-900 ${A.num}`}
                    >
                      {money(customerTotal)}
                    </div>
                  </div>

                  {/* Expanded invoices */}
                  {expanded[rec.customer_id] && (
                    <div className="mt-3 border-t border-gray-100 pt-2 max-h-[18rem] overflow-auto">
                      <div className="mb-2">
                        <div className={A.label}>{t("receivables.outstandingInvoices")}</div>

                        <div className="mt-2 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={toggleInvoiceSortDue}
                            className={sortPillBtnCls(
                              invoiceSort.key === "due",
                            )}
                            title={t("receivables.sortDueDate")}
                          >
                            {t("sales.common.dueDate")}{" "}
                            <span className="ml-1 text-gray-400">
                              {invoiceSort.key === "due"
                                ? invoiceSort.dir === "asc"
                                  ? "↑"
                                  : "↓"
                                : (
                                  <ArrowsUpDownIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                )}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={toggleInvoiceSortAmount}
                            className={sortPillBtnCls(
                              invoiceSort.key === "amount",
                            )}
                            title={t("receivables.sortAmountDue")}
                          >
                            {t("sales.common.amount")}{" "}
                            <span className="ml-1 text-gray-400">
                              {invoiceSort.key === "amount"
                                ? invoiceSort.dir === "asc"
                                  ? "↑"
                                  : "↓"
                                : (
                                  <ArrowsUpDownIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                    focusable="false"
                                  />
                                )}
                            </span>
                          </button>
                        </div>
                      </div>

                      {invoices.length ? (
                        <>
                          {displayInvoices.map((inv) => {
                            const pill = statusPill(inv.status);
                            return (
                              <div
                                key={inv.invoice_id}
                                className="py-2 flex items-start justify-between border-b border-gray-100 last:border-none cursor-pointer hover:bg-gray-50 rounded-xl"
                                onClick={() =>
                                  navigate(
                                    companyRoute(
                                      company_id,
                                      `/sales/${inv.sale_id}`,
                                    ),
                                  )
                                }
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    navigate(
                                      companyRoute(
                                        company_id,
                                        `/sales/${inv.sale_id}`,
                                      ),
                                    );
                                }}
                                aria-label={t("receivables.viewSale", { id: inv.sale_id })}
                              >
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-800 whitespace-normal [overflow-wrap:anywhere]">
                                    {inv.invoice_number}
                                  </div>
                                  <div className={`${A.label} text-gray-500`}>
                                    {formatDate(inv.due_date)}
                                  </div>
                                </div>

                                <div className="flex flex-col items-end">
                                  <div
                                    className={`font-medium text-gray-900 ${A.num}`}
                                  >
                                    {money(inv.amount_due)}
                                  </div>
                                  <span className={`mt-1 ${pill.cls}`}>
                                    {pill.label}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {invoices.length > PREVIEW_MOBILE ? (
                            <button
                              type="button"
                              onClick={() =>
                                setShowAllInvoices((m) => ({
                                  ...m,
                                  [rec.customer_id]: !showAll,
                                }))
                              }
                              className={`${linkCls} mt-2`}
                            >
                              {showAll
                                ? t("receivables.showLess")
                                : t("receivables.showAll", { count: invoices.length })}
                            </button>
                          ) : null}
                        </>
                      ) : (
                        <div className="text-[13px] text-gray-500">
                          {t("receivables.noInvoices")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination (mobile) */}
          <div
            className={`flex md:hidden justify-between items-center mt-4 w-full ${PAGE_W} px-2`}
          >
            {pagerText}
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnCls}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                type="button"
              >
                {t("directory.common.prev")}
              </Button>
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnCls}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                type="button"
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>

          {/* Desktop Table */}
          <div className={`hidden md:block w-full ${PAGE_W} mx-auto px-2 mt-3`}>
            <ListTableShell>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b">
                    <th className="w-8" />

                    <th className={`${thCls} text-left`}>
                      <button
                        type="button"
                        onClick={() => onHeaderSort("customer")}
                        className={thBtnCls}
                        title={t("receivables.sortCustomer")}
                      >
                        {t("sales.common.customer")} {sortArrow("customer")}
                      </button>
                    </th>

                    <th className={`${thCls} text-right`}>
                      <button
                        type="button"
                        onClick={() => onHeaderSort("overdue")}
                        className={thBtnCls}
                        title={t("receivables.sortOverdue")}
                      >
                        {t("purchases.status.overdue")} {sortArrow("overdue")}
                      </button>
                    </th>

                    <th className={`${thCls} text-right`}>{t("receivables.aging10")}</th>
                    <th className={`${thCls} text-right`}>{t("receivables.aging20")}</th>
                    <th className={`${thCls} text-right`}>{t("receivables.aging30")}</th>

                    <th className="py-2 pl-4 pr-6 text-right text-xs text-gray-500">
                      <button
                        type="button"
                        onClick={() => onHeaderSort("total")}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        title={t("receivables.sortTotalDue")}
                      >
                        {t("sales.common.total")} {sortArrow("total")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pagedReceivables.map((rec) => {
                    const customerTotal = sumCustomerDue(rec);

                    const invoices = sortInvoices(
                      uniqueInvoices(rec.invoices),
                      invoiceSort,
                    );
                    const showAll = !!showAllInvoices[rec.customer_id];
                    const displayInvoices = showAll
                      ? invoices
                      : invoices.slice(0, PREVIEW_DESKTOP);

                    return (
                      <React.Fragment key={rec.customer_id}>
                        <tr
                          className="border-b hover:bg-gray-50 text-gray-700 transition cursor-pointer"
                          onClick={() => toggleExpanded(rec.customer_id)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              toggleExpanded(rec.customer_id);
                          }}
                        >
                          <td className="py-2">
                            {expanded[rec.customer_id] ? (
                              <ChevronDownIcon className="w-4 h-4 text-appleblue" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                            )}
                          </td>

                          <td className="py-2 max-w-[14rem] truncate font-medium text-gray-900">
                            {rec.name}
                          </td>

                          <td className={tdNumCls}>
                            {rec.aging_overdue > 0
                              ? money(rec.aging_overdue)
                              : "—"}
                          </td>
                          <td className={tdNumCls}>
                            {rec.aging_10 > 0 ? money(rec.aging_10) : "—"}
                          </td>
                          <td className={tdNumCls}>
                            {rec.aging_20 > 0 ? money(rec.aging_20) : "—"}
                          </td>
                          <td className={tdNumCls}>
                            {rec.aging_30 > 0 ? money(rec.aging_30) : "—"}
                          </td>

                          <td className={tdTotalCls}>{money(customerTotal)}</td>
                        </tr>

                        {expanded[rec.customer_id] && invoices.length > 0 && (
                          <tr className="bg-gray-50">
                            <td />
                            <td colSpan={6} className="p-0">
                              <div className="pl-2 py-3">
                                <div className="max-h-[22rem] overflow-auto rounded-xl">
                                  <table className="w-full table-fixed text-[13px]">
                                    <thead>
                                      <tr className="text-xs text-gray-500">
                                        <th className="w-1/4 text-left py-1">
                                          {t("sales.common.invoiceNumber")}
                                        </th>

                                        <th className="w-1/4 text-left py-1">
                                          <button
                                            type="button"
                                            onClick={toggleInvoiceSortDue}
                                            className="inline-flex items-center gap-1 hover:text-gray-700"
                                            title={t("receivables.toggleDueDateSort")}
                                          >
                                            {t("sales.common.dueDate")}{" "}
                                            {invoiceSort.key === "due" ? (
                                              <span className="text-[11px] text-gray-400">
                                                {invoiceSort.dir === "asc"
                                                  ? "↑"
                                                  : "↓"}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] text-gray-300">
                                                ↕
                                              </span>
                                            )}
                                          </button>
                                        </th>

                                        <th className="w-1/4 text-right py-1 pl-4 pr-6">
                                          <button
                                            type="button"
                                            onClick={toggleInvoiceSortAmount}
                                            className="inline-flex items-center gap-1 hover:text-gray-700"
                                            title={t("receivables.toggleAmountDueSort")}
                                          >
                                            {t("receivables.amountDue")}{" "}
                                            {invoiceSort.key === "amount" ? (
                                              <span className="text-[11px] text-gray-400">
                                                {invoiceSort.dir === "asc"
                                                  ? "↑"
                                                  : "↓"}
                                              </span>
                                            ) : (
                                              <span className="text-[11px] text-gray-300">
                                                ↕
                                              </span>
                                            )}
                                          </button>
                                        </th>

                                        <th className="w-1/4 text-center py-1 pl-4 pr-6">
                                          {t("sales.common.status")}
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {displayInvoices.map((inv) => {
                                        const pill = statusPill(inv.status);
                                        return (
                                          <tr
                                            key={inv.invoice_id}
                                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                            onClick={() =>
                                              navigate(
                                                companyRoute(
                                                  company_id,
                                                  `/sales/${inv.sale_id}`,
                                                ),
                                              )
                                            }
                                          >
                                            <td className="py-2 pr-2 font-medium text-gray-800 [overflow-wrap:anywhere]">
                                              {inv.invoice_number}
                                            </td>
                                            <td className="py-2 pr-2 text-gray-600">
                                              {formatDate(inv.due_date)}
                                            </td>
                                            <td
                                              className={`py-2 pl-4 pr-6 text-right font-medium text-gray-900 ${A.num}`}
                                            >
                                              {money(inv.amount_due)}
                                            </td>
                                            <td className="py-2 pl-4 pr-6 text-center">
                                              <span className={pill.cls}>
                                                {pill.label}
                                              </span>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>

                                {invoices.length > PREVIEW_DESKTOP ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowAllInvoices((m) => ({
                                        ...m,
                                        [rec.customer_id]: !showAll,
                                      }))
                                    }
                                    className={`${linkCls} mt-2`}
                                  >
                                    {showAll
                                      ? t("receivables.showLess")
                                      : t("receivables.showAll", { count: invoices.length })}
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </ListTableShell>

            {/* Pagination (desktop) */}
            <div className="hidden md:flex justify-between items-center mt-4">
              {pagerText}
              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnCls}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  type="button"
                >
                  {t("directory.common.prev")}
                </Button>
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnCls}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  type="button"
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
